import { apiClient, TRANSFER_TIMEOUT_MS } from "./client"

import type { GradingAcceptPath, GradingOutcome } from "@/constants/grading"
import type { ProgressUpload } from "@/types"

// PRD-20 — photo-grading endpoints. The flow is:
//   1. POST /api/progress/uploads/grade  (multipart, 1-3 images)
//      → backend writes to GCS + dispatches `auto_grade_submission`.
//   2. Realtime `grade.phase` events drive the phased progress bar.
//   3. GET /api/progress/uploads/:id/grading is the cold-load / poll
//      fallback if the WebSocket dropped.
//   4. PATCH /api/progress/uploads/:id/grading saves the professor's
//      confirmed score; backend writes ProgressRecord + audit row.

export interface CreateGradingUploadPayload {
  task_id: string
  /**
   * PRD-36 §FR-3.x — omit to let the backend resolve the student from the
   * `Email:` row the engine prints in the sheet header.
   */
  student_id?: string
  files: File[]
  /**
   * PRD-36 §FR-1.12 — acknowledge that this (task, student) already has a
   * grade and that it should be replaced. Without it the endpoint answers
   * 409 `upload_regrade_requires_confirmation` rather than overwriting.
   */
  force?: boolean
}

export interface GradingOverridePayload {
  index: number
  detected?: string
  outcome?: GradingOutcome
}

export interface SaveGradingPayload {
  final_score: number
  accept_path: GradingAcceptPath
  per_question_overrides?: GradingOverridePayload[]
}

export const gradingApi = {
  /**
   * Multipart upload of 1–3 images for a (task, student) pair. The
   * task's parent Material must be of type `examen` — the backend
   * 422s otherwise. Frontend pre-rejects oversize + wrong-MIME
   * before this call (see lib/gradingFiles.ts); the backend re-validates.
   */
  createUpload: (payload: CreateGradingUploadPayload) => {
    const form = new FormData()
    form.append("task_id", payload.task_id)
    if (payload.student_id) form.append("student_id", payload.student_id)
    for (const f of payload.files) {
      form.append("files", f)
    }
    if (payload.force) form.append("force", "true")
    return apiClient
      .post<ProgressUpload>("/progress/uploads/grade", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TRANSFER_TIMEOUT_MS,
      })
      .then((r) => r.data)
  },

  /** Cold-load / poll fallback. Realtime `grade.phase` is the primary signal. */
  get: (uploadId: string) =>
    apiClient.get<ProgressUpload>(`/progress/uploads/${uploadId}/grading`).then((r) => r.data),

  /** Save the professor's confirmed score. Idempotent via the `final` snapshot. */
  save: (uploadId: string, payload: SaveGradingPayload) =>
    apiClient
      .patch<ProgressUpload>(`/progress/uploads/${uploadId}/grading`, payload)
      .then((r) => r.data),

  /** PRD-20 v1.4 §FR-66 — hard-delete a non-terminal upload ("Quitar envío").
   *  Backend refuses GRADED uploads with 422 `upload_already_graded`. */
  deleteUpload: (uploadId: string) =>
    apiClient.delete(`/progress/uploads/${uploadId}`).then((r) => r.data),

  /** PRD-20 v1.4 §FR-66 — list signed URLs for each image in a grading
   *  upload, used by the <UploadPreviewLightbox>. One round-trip on
   *  modal open; signed URLs expire in 5 minutes. */
  listFiles: (uploadId: string) =>
    apiClient
      .get<{ items: UploadPreviewItem[] }>(`/progress/uploads/${uploadId}/files`)
      .then((r) => r.data.items),
}

export interface UploadPreviewItem {
  seq: number
  url: string
  content_type: string
}
